import { parseISO, addDays, startOfDay, getDay, format } from "date-fns";
import { NFB_SERVICE, PSY_SERVICE } from "../../utils/constants";
import { DAYS_OF_WEEK } from "../../screens/orders/moloni/scheduled-invoices/utils";
import { activeServicesProvidedFilter, plannedServicesProvidedFilter } from "../../screens/treatments/treatment-prescription/utils";
import { PreferredDaySchedule, PreferredSchedule, PreferredTimeSlot, ServicePeriodicity, SessionsOptions } from "./types";
import { BOOKED_STATUSES, PREFERRED_SCHEDULE_SESSIONS_COUNT } from "./constants";

export const isNext7Days = (targetDate: Date) => {
    const startDate = new Date();
    const endDate = addDays(new Date(), 7);
    return targetDate >= startDate && targetDate <= endDate;
};

export const getSessions = (items: any, category: string | null, options?: SessionsOptions, serviceId?: number) => {
    if (options && options.sessionDone) {
        if (serviceId) {
            return items.filter((item: any) => item?.service?.id === serviceId && item.status === "done")?.length || 0;
        }
        return items.filter((item: any) => item?.service?.category === category && item.status === "done")?.length || 0;
    } else if (options && options.next7Days) {
        return (
            items.filter((item: any) => isNext7Days(parseISO(item?.start_date)) && item?.service?.category === category)
                .length || 0
        );
    } else {
        //return items.filter((item: any) => item.order_item?.service?.category === category)?.length || 0;
        console.log("Session Preview, removed an else statemente - if problems check") // TODO remove if no problems
    	return 0
    }
};

export const getOrderItems = (items: any, category: string) => {
    const paidItems = items.filter((item: any) => {
        const service = item?.service_variant?.service;
        return service?.category === category;
    });

    const totalQuantity = paidItems?.reduce((acc: number, item: any) => {
        return acc + item.quantity;
    }, 0);

    return totalQuantity;
};

export const createSessionsFilter = (parentId: string | number = "", treatmentId?: string | number) => {
    if (treatmentId) {
        return {
            treatment: {
                _eq: treatmentId,
            },
        };
    } else {
        return {
            treatment: {
                _and: [
					{ client: { _eq: parentId } },
                    { treatment_status_new: { _in: ["all_good", "waiting_start_of_treatment", "waiting_report", "sporadic"] } }
                ],
            },
        };
    }
};

export const createOrderItemsFilter = (parentId: string | number = "", treatmentId?: string | number) => {
    if (treatmentId) {
        return {
			order_id: {
				_and: [
					{ treatment: { _eq: treatmentId } },
					{ payment_status: { _eq: "paid" } },
				],
			},
		};
    } else {
        return {
            order_id: {
                _and: [
                    { treatment: { client: { _eq: parentId } } },
                    { payment_status: { _eq: "paid" } },
					{ treatment: { treatment_status_new: { _in: ["all_good", "waiting_start_of_treatment", "waiting_report", "sporadic"] } } }
                ],
            },
        };
    }
};

export const getBookedSessions = (sessions: Array<any>, serviceId: number) => {
    const today = startOfDay(new Date());
    const bookedSessions = sessions.filter((session) => {
        const isCorrectService = session?.service?.id === serviceId;
        if (!isCorrectService) return false;

        const isDoneBeforeToday = session.status === "done" && parseISO(session.start_date) <= today;
        const isBookedStatus = BOOKED_STATUSES.includes(session.status);
        return isDoneBeforeToday || isBookedStatus;
    });
    return bookedSessions.length;
};

export const getBookedSessionsForMultipleServices = (sessions: Array<any>, serviceIds: Array<number>) => {
    const today = startOfDay(new Date());
    const bookedSessions = sessions.filter((session) => {
        const isCorrectService = serviceIds.includes(session?.service?.id);
        if (!isCorrectService) return false;

        const isDoneBeforeToday = session.status === "done" && parseISO(session.start_date) <= today;
        const isBookedStatus = BOOKED_STATUSES.includes(session.status);
        return isDoneBeforeToday || isBookedStatus;
    });
    return bookedSessions.length;
};

export const getLatestBookedSessionDate = (sessions: Array<any>, serviceId: number): string | null => {
    const bookedSessions = sessions.filter((session) => {
        const isCorrectService = session?.service?.id === serviceId;
        const isBookedStatus = BOOKED_STATUSES.includes(session.status);
        return isCorrectService && isBookedStatus;
    });

    if (bookedSessions.length === 0) return null;

    const sortedSessions = bookedSessions.sort((a, b) =>
        parseISO(b.start_date).getTime() - parseISO(a.start_date).getTime()
    );
    return sortedSessions[0].start_date;
};

export const getLatestBookedSessionDateForMultipleServices = (sessions: Array<any>, serviceIds: Array<number>): string | null => {
    const bookedSessions = sessions.filter((session) => {
        const isCorrectService = serviceIds.includes(session?.service?.id);
        const isBookedStatus = BOOKED_STATUSES.includes(session.status);
        return isCorrectService && isBookedStatus;
    });

    if (bookedSessions.length === 0) return null;

    const sortedSessions = bookedSessions.sort((a, b) =>
        parseISO(b.start_date).getTime() - parseISO(a.start_date).getTime()
    );
    return sortedSessions[0].start_date;
};

export const getPrescribedMaxSessions = (servicesPrescribed: Array<any>, serviceId: number) => {
    const servicePrescription = servicesPrescribed.find((sp) => sp?.service?.id === serviceId);
    if (!servicePrescription) return 0;

    const estimatedSessions = servicePrescription.estimated_total_sessions || 0;
    if (estimatedSessions <= 10) return estimatedSessions;

    const sessionToAdd = serviceId === NFB_SERVICE ? 5 : 10;
    return estimatedSessions + sessionToAdd;
};

export const getPrescribedMaxSessionsForMultipleServices = (servicesPrescribed: Array<any>, serviceIds: Array<number>) => {
    const totalPrescribed = serviceIds.reduce((total, serviceId) => {
        const prescribedForService = getPrescribedMaxSessions(servicesPrescribed, serviceId);
        return total + prescribedForService;
    }, 0);
    return totalPrescribed;
};

const getPreferredScheduleForService = (sessions: Array<any>, serviceId: number): Array<PreferredDaySchedule> => {
    const doneSessions = sessions
        .filter((session) => session?.service?.id === serviceId && session.status === "done")
        .sort((a, b) => parseISO(b.start_date).getTime() - parseISO(a.start_date).getTime())
        .slice(0, PREFERRED_SCHEDULE_SESSIONS_COUNT);

    if (doneSessions.length === 0) return [];

    const dayMap = new Map<number, Map<string, number>>();

    doneSessions.forEach((session) => {
        const sessionDate = parseISO(session.start_date);
        const dayOfWeek = getDay(sessionDate);
        const time = format(sessionDate, "HH:mm");

        if (!dayMap.has(dayOfWeek)) {
            dayMap.set(dayOfWeek, new Map());
        }
        const timesMap = dayMap.get(dayOfWeek)!;
        const currentCount = timesMap.get(time) || 0;
        timesMap.set(time, currentCount + 1);
    });

    const preferredDays: Array<PreferredDaySchedule> = Array.from(dayMap.entries()).map(([dayOfWeek, timesMap]) => {
        const dayInfo = DAYS_OF_WEEK.find((d) => d.value === dayOfWeek);
        const times: Array<PreferredTimeSlot> = Array.from(timesMap.entries())
            .map(([time, count]) => ({ time, count }))
            .sort((a, b) => a.time.localeCompare(b.time));

        return {
            dayOfWeek,
            dayName: dayInfo?.label.replace("-feira", "") || "",
            times,
        };
    });

    const sortedByTotalCount = preferredDays.sort((a, b) => {
        const totalA = a.times.reduce((sum, t) => sum + t.count, 0);
        const totalB = b.times.reduce((sum, t) => sum + t.count, 0);
        return totalB - totalA;
    });

    return sortedByTotalCount;
};

const getServicePeriodicity = (servicesProvided: Array<any>, serviceId: number): ServicePeriodicity => {
    if (!servicesProvided || servicesProvided.length === 0) return null;

    const serviceProvidedForService = servicesProvided.filter((sp) => sp.service === serviceId);
    if (serviceProvidedForService.length === 0) return null;

    const activeServiceProvided = serviceProvidedForService
        .filter(activeServicesProvidedFilter)
        .sort((a, b) => parseISO(b.date_begin).getTime() - parseISO(a.date_begin).getTime())[0];

    if (activeServiceProvided) {
        return {
            quantity: activeServiceProvided.session_quantity,
            periodicity: activeServiceProvided.session_periodicity,
            status: "active",
        };
    }

    const plannedServiceProvided = serviceProvidedForService
        .filter(plannedServicesProvidedFilter)
        .sort((a, b) => parseISO(a.date_begin).getTime() - parseISO(b.date_begin).getTime())[0];

    if (plannedServiceProvided) {
        return {
            quantity: plannedServiceProvided.session_quantity,
            periodicity: plannedServiceProvided.session_periodicity,
            status: "planned",
        };
    }

    return {
        quantity: 0,
        periodicity: "",
        status: "finished",
    };
};

export const getPreferredSchedule = (sessions: Array<any>, servicesProvided: Array<any>): PreferredSchedule => {
    const nfbPreferred = getPreferredScheduleForService(sessions, NFB_SERVICE);
    const psyPreferred = getPreferredScheduleForService(sessions, PSY_SERVICE);
    const nfbPeriodicity = getServicePeriodicity(servicesProvided, NFB_SERVICE);
    const psyPeriodicity = getServicePeriodicity(servicesProvided, PSY_SERVICE);

    return {
        nfb: nfbPreferred,
        psy: psyPreferred,
        nfbPeriodicity,
        psyPeriodicity,
        sessionsAnalyzed: PREFERRED_SCHEDULE_SESSIONS_COUNT,
    };
};
